import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight, Clock, Calendar } from 'lucide-react';

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

interface Props {
  value: string; // ISO datetime string or ''
  onChange: (iso: string) => void;
  label?: string;
  placeholder?: string;
  minDate?: Date;
}

function pad(n: number) { return n.toString().padStart(2, '0'); }

function isoToLocal(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

function formatDisplay(d: Date): string {
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
}

export const DateTimePicker: React.FC<Props> = ({ value, onChange, label, placeholder = 'Select date & time', minDate }) => {
  const parsed = isoToLocal(value);
  const now = new Date();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'calendar' | 'time'>('calendar');
  const [calYear, setCalYear] = useState((parsed || now).getFullYear());
  const [calMonth, setCalMonth] = useState((parsed || now).getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(parsed);
  const [hour, setHour] = useState(parsed ? (parsed.getHours() % 12 || 12) : 10);
  const [minute, setMinute] = useState(parsed ? parsed.getMinutes() : 0);
  const [ampm, setAmpm] = useState<'AM' | 'PM'>(parsed ? (parsed.getHours() >= 12 ? 'PM' : 'AM') : 'AM');

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const p = isoToLocal(value);
    setSelectedDate(p);
    if (p) {
      setCalYear(p.getFullYear());
      setCalMonth(p.getMonth());
      setHour(p.getHours() % 12 || 12);
      setMinute(p.getMinutes());
      setAmpm(p.getHours() >= 12 ? 'PM' : 'AM');
    }
  }, [value]);

  const openPicker = () => {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    setView('calendar');
    setOpen(true);
  };

  const commit = (date: Date, h: number, m: number, ap: 'AM' | 'PM') => {
    const result = new Date(date);
    let hours24 = h % 12;
    if (ap === 'PM') hours24 += 12;
    result.setHours(hours24, m, 0, 0);
    onChange(result.toISOString());
  };

  const selectDay = (d: Date) => {
    setSelectedDate(d);
    commit(d, hour, minute, ampm);
    setView('time');
  };

  const applyTime = () => {
    if (selectedDate) commit(selectedDate, hour, minute, ampm);
    setOpen(false);
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const getDays = () => {
    const first = new Date(calYear, calMonth, 1).getDay();
    const total = new Date(calYear, calMonth + 1, 0).getDate();
    const cells: (number | null)[] = Array(first).fill(null);
    for (let i = 1; i <= total; i++) cells.push(i);
    return cells;
  };

  const isSelected = (day: number) =>
    selectedDate &&
    selectedDate.getFullYear() === calYear &&
    selectedDate.getMonth() === calMonth &&
    selectedDate.getDate() === day;

  const isDisabled = (day: number) => {
    if (!minDate) return false;
    const d = new Date(calYear, calMonth, day);
    d.setHours(0, 0, 0, 0);
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    return d < min;
  };

  const isToday = (day: number) => {
    const t = new Date();
    return t.getFullYear() === calYear && t.getMonth() === calMonth && t.getDate() === day;
  };

  // Placement: prefer below, flip to above if not enough space
  const getStyle = (): React.CSSProperties => {
    if (!rect) return { position: 'fixed', top: 0, left: 0 };
    const W = 320;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const top = spaceBelow >= 340 || spaceBelow >= spaceAbove
      ? rect.bottom + 6
      : rect.top - 348;
    const left = Math.min(rect.left, window.innerWidth - W - 8);
    return { position: 'fixed', top, left, width: W, zIndex: 9999 };
  };

  const portal = document.getElementById('modal-root') || document.body;

  return (
    <div className="flex flex-col gap-1.5">
      {label && <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</label>}
      <button
        ref={triggerRef}
        type="button"
        onClick={openPicker}
        className="form-input flex items-center gap-2 text-left cursor-pointer w-full"
      >
        <Calendar size={15} className="text-slate-400 shrink-0" />
        <span className={`flex-1 text-sm ${!selectedDate ? 'text-slate-400' : 'text-slate-800 dark:text-white'}`}>
          {selectedDate ? formatDisplay(selectedDate) : placeholder}
        </span>
        {selectedDate && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onChange(''); setSelectedDate(null); }}
            className="text-slate-300 hover:text-slate-500 dark:hover:text-slate-300 transition-colors ml-1 shrink-0 text-lg leading-none"
          >×</button>
        )}
      </button>

      {open && ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />

          <div style={getStyle()} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl shadow-slate-900/20 overflow-hidden">

            {/* Tab bar */}
            <div className="flex border-b border-slate-100 dark:border-white/5">
              <button
                type="button"
                onClick={() => setView('calendar')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors ${view === 'calendar' ? 'text-primary-500 border-b-2 border-primary-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
              >
                <Calendar size={13} /> Date
              </button>
              <button
                type="button"
                onClick={() => setView('time')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors ${view === 'time' ? 'text-primary-500 border-b-2 border-primary-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
              >
                <Clock size={13} /> Time
              </button>
            </div>

            {/* Calendar view */}
            {view === 'calendar' && (
              <div className="p-4">
                {/* Month nav */}
                <div className="flex items-center justify-between mb-4">
                  <button type="button" onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors text-slate-500 dark:text-slate-400">
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm font-bold text-slate-800 dark:text-white">{MONTHS[calMonth]} {calYear}</span>
                  <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors text-slate-500 dark:text-slate-400">
                    <ChevronRight size={16} />
                  </button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 mb-1">
                  {DAYS.map(d => (
                    <div key={d} className="text-center text-[10px] font-bold text-slate-400 uppercase py-1">{d}</div>
                  ))}
                </div>

                {/* Day cells */}
                <div className="grid grid-cols-7 gap-y-0.5">
                  {getDays().map((day, idx) => {
                    if (!day) return <div key={idx} />;
                    const sel = isSelected(day);
                    const dis = isDisabled(day);
                    const tod = isToday(day);
                    return (
                      <button
                        key={idx}
                        type="button"
                        disabled={dis}
                        onClick={() => selectDay(new Date(calYear, calMonth, day))}
                        className={`
                          aspect-square w-full flex items-center justify-center rounded-lg text-xs font-medium transition-all
                          ${dis ? 'text-slate-200 dark:text-slate-700 cursor-not-allowed' : 'cursor-pointer'}
                          ${sel ? 'bg-primary-500 text-white font-bold shadow-sm shadow-primary-500/30' : ''}
                          ${!sel && tod ? 'bg-primary-500/10 text-primary-500 font-bold' : ''}
                          ${!sel && !dis ? 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-slate-200' : ''}
                        `}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>

                {/* Quick actions */}
                <div className="flex justify-between mt-4 pt-3 border-t border-slate-100 dark:border-white/5">
                  <button type="button" onClick={() => { onChange(''); setSelectedDate(null); setOpen(false); }} className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">Clear</button>
                  <button type="button" onClick={() => selectDay(new Date())} className="text-xs text-primary-500 hover:text-primary-600 font-semibold transition-colors">Today</button>
                </div>
              </div>
            )}

            {/* Time view */}
            {view === 'time' && (
              <div className="p-5">
                <div className="flex items-center justify-center gap-3">
                  {/* Hours */}
                  <div className="flex flex-col items-center gap-1">
                    <button type="button" onClick={() => setHour(h => h === 12 ? 1 : h + 1)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                      <ChevronRight size={16} className="-rotate-90" />
                    </button>
                    <div className="w-16 h-14 flex items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-white/5">
                      <span className="text-2xl font-bold text-slate-800 dark:text-white tabular-nums">{pad(hour)}</span>
                    </div>
                    <button type="button" onClick={() => setHour(h => h === 1 ? 12 : h - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                      <ChevronRight size={16} className="rotate-90" />
                    </button>
                  </div>

                  <span className="text-2xl font-bold text-slate-300 dark:text-slate-600 mb-0.5">:</span>

                  {/* Minutes */}
                  <div className="flex flex-col items-center gap-1">
                    <button type="button" onClick={() => setMinute(m => m === 59 ? 0 : m + 1)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                      <ChevronRight size={16} className="-rotate-90" />
                    </button>
                    <div className="w-16 h-14 flex items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-white/5">
                      <span className="text-2xl font-bold text-slate-800 dark:text-white tabular-nums">{pad(minute)}</span>
                    </div>
                    <button type="button" onClick={() => setMinute(m => m === 0 ? 59 : m - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                      <ChevronRight size={16} className="rotate-90" />
                    </button>
                  </div>

                  {/* AM/PM */}
                  <div className="flex flex-col gap-1 ml-1">
                    {(['AM', 'PM'] as const).map(ap => (
                      <button
                        key={ap}
                        type="button"
                        onClick={() => setAmpm(ap)}
                        className={`w-14 py-2 rounded-xl text-xs font-bold transition-all ${
                          ampm === ap
                            ? 'bg-primary-500 text-white shadow-sm shadow-primary-500/30'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                        }`}
                      >
                        {ap}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Minute quick picks */}
                <div className="flex justify-center gap-2 mt-4">
                  {[0, 15, 30, 45].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMinute(m)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                        minute === m ? 'bg-primary-500/10 text-primary-500' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      :{pad(m)}
                    </button>
                  ))}
                </div>

                {selectedDate && (
                  <p className="text-center text-[11px] text-slate-400 mt-3">
                    {selectedDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    &nbsp;&middot;&nbsp;{pad(hour)}:{pad(minute)} {ampm}
                  </p>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 dark:border-white/5">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary text-xs px-4 py-1.5">Cancel</button>
              <button
                type="button"
                disabled={!selectedDate}
                onClick={applyTime}
                className="btn-primary text-xs px-4 py-1.5 disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        </>,
        portal
      )}
    </div>
  );
};

export default DateTimePicker;
